import Head from 'next/head';
import { useEffect } from 'react';
import { cloneDeepWith } from 'lodash-es';
import { CoreGlobalFooter } from '@care/navigation';
import { Grid, Container, Box, useMediaQuery, Link, Typography, type Theme } from '@mui/material';

import { UsStateCode } from '@/__generated__/globalTypes';
import {
  seoSegmentationsByCityOrState_seoSegmentationsByCityOrState_SEOSegmentationsByCityOrStateSuccess_seoSegmentationsByCityOrState_linkSection as SEODirectorySection,
  seoSegmentationsByCityOrState_seoSegmentationsByCityOrState_SEOSegmentationsByCityOrStateSuccess_seoSegmentationsByCityOrState_linkSection_section_linkList as SEOSegmentationLink,
  seoSegmentationsByCityOrState_seoSegmentationsByCityOrState_SEOSegmentationsByCityOrStateSuccess_seoSegmentationsByCityOrState_linkSection_section as SEODirectorySubSection,
} from '@/__generated__/seoSegmentationsByCityOrState';
import { getSEOSegmentationBreadcrumbs_getSEOSegmentationBreadcrumbs_breadcrumbs as BreadcrumbsType } from '@/__generated__/getSEOSegmentationBreadcrumbs';
import {
  AmplitudeEvent,
  trackAmplitudeEvent,
  setDeviceDataFromHeaders,
} from '@/lib/analyticsHelper';
import NavBar from '@/components/SeoGlobalNavbar';
import columnarLinkedList from '@/utils/columnarLinkedList';
import { DirectorySection, DirectorySubSections } from '@/constants';
import Breadcrumbs from '@/components/Breadcrumbs';

export type CityServicesDirectoryProps = {
  props: {
    pageData: {
      breadcrumbs?: BreadcrumbsType[];
      linkSection: SEODirectorySection[];
      state: UsStateCode;
      city: string | null;
    };
    seoSegmentId: string;
    deviceChar?: any;
  };
};

const CityServicesDirectory = ({ props }: CityServicesDirectoryProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const numOfColumns = isMobile ? 1 : 3;
  const { linkSection, city, state, breadcrumbs } = props.pageData;
  const { deviceChar } = props;
  const stringifyDeviceChar = JSON.stringify(deviceChar);

  const trackPageViewedEvent = async () => {
    const amplitudeDataForPageViewed = {
      screen_name: 'SEO Directory Page',
      seo_page_title: `Browse Care.com Local Services in ${city}, ${state}`,
      seo_page_type: 'SEO Directory page',
      page_template: 'City Services Directory',
      seo_page_id: 1443,
    };

    await trackAmplitudeEvent(AmplitudeEvent.seoPageViewed, amplitudeDataForPageViewed);
  };

  useEffect(() => {
    trackPageViewedEvent();
  });

  useEffect(() => {
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setDeviceDataFromHeaders(deviceChar);
    }
  }, [stringifyDeviceChar, deviceChar]);

  const subSectionLinkList = (linkList: SEOSegmentationLink[]): JSX.Element[] => {
    return linkList.map((link: SEOSegmentationLink) => {
      return (
        <Box key={link.content} pb={1.25}>
          <Link
            variant="link2"
            href={link.url}
            sx={{
              ':hover': {
                textDecoration: 'none',
              },
            }}>
            {link.content}
          </Link>
        </Box>
      );
    });
  };

  const renderSubSection = (section: SEODirectorySubSection[]): JSX.Element[] => {
    return section.map(({ subSectionId, subSectionTitle, linkList }) => {
      const segmentationList = subSectionLinkList(linkList);

      return (
        <Box key={subSectionId} width="300px" mr={{ sm: 3, md: 6, lg: 10 }}>
          <Box pb={1.25}>
            {subSectionTitle.__typename === 'SEOLinkSectionTitle' && (
              <Typography variant="h3">{subSectionTitle.title}</Typography>
            )}
            {subSectionTitle.__typename === 'SEOLinkSectionLinkedTitle' && (
              <Link
                key={subSectionTitle.linkedTitle.content}
                variant="h3"
                href={subSectionTitle.linkedTitle.url}
                sx={{
                  ':hover': {
                    textDecoration: 'none',
                  },
                }}>
                {subSectionTitle.linkedTitle.content}
              </Link>
            )}
          </Box>

          <Box display="flex" flexDirection="column">
            {segmentationList}
          </Box>
        </Box>
      );
    });
  };

  const sortSubSection = (section: SEODirectorySubSection[]): SEODirectorySubSection[] => {
    const sortOrder: { [key: string]: number } = {
      [DirectorySubSections.BABYSITTERS]: 1,
      [DirectorySubSections.NANNY]: 2,
      [DirectorySubSections.PET_CARE]: 3,
      [DirectorySubSections.CHILD_CARE]: 4,
      [DirectorySubSections.SENIOR_CARE]: 5,
      [DirectorySubSections.TUTORS]: 6,
      [DirectorySubSections.HOUSE_KEEPING]: 7,
      [DirectorySubSections.CARE_GIGS]: 8,
      [DirectorySubSections.SPECIAL_NEEDS]: 9,
    };

    return cloneDeepWith(section).sort(
      (subSectionA: SEODirectorySubSection, subSectionB: SEODirectorySubSection) => {
        return sortOrder[subSectionA.subSectionId] - sortOrder[subSectionB.subSectionId];
      }
    );
  };

  const segmentationsDirectory: JSX.Element[] = linkSection.map(
    ({ sectionId, section }: SEODirectorySection) => {
      let subSections = section.filter(
        (subSection: SEODirectorySubSection) => subSection.linkList.length > 0
      );

      const columnarSubSections = columnarLinkedList(subSections, numOfColumns);
      if (sectionId === DirectorySection.PROVIDERS) {
        subSections = sortSubSection(subSections);
      }

      const renderSubSections = columnarSubSections.map(
        (subSectionRow: SEODirectorySubSection[], index) => {
          const directorySubSection = renderSubSection(subSectionRow);

          return (
            <Box
              display="flex"
              key={`row-${index + 1}`}
              flexDirection={{ xs: 'column', sm: 'row' }}
              mb={6.25}>
              {directorySubSection}
            </Box>
          );
        }
      );

      return <div key={sectionId}>{renderSubSections}</div>;
    }
  );

  return (
    <>
      <Head>
        <title>{`Browse Local Services in ${city}, ${state} | Care.com`} </title>
        <meta name="description" content={`Find ${city}, ${state} services on Care.com`} />
      </Head>
      <NavBar enrollmentURL="/app/vhp/get-started" />
      <Container maxWidth="lg">
        <Grid container>
          <Grid item xs={12}>
            <Box mt={5} mb={3}>
              {breadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={4}>
              <Typography variant="h2" component="h1">
                {`Browse Care.com Local Services in ${city}, ${state}`}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <div>{segmentationsDirectory}</div>
          </Grid>
        </Grid>
      </Container>
      <CoreGlobalFooter />
    </>
  );
};

export default CityServicesDirectory;
